import { checkboxClasses, dividerClasses, inputBaseClasses, menuItemClasses, outlinedInputClasses, selectClasses } from '@mui/material';
import { PaletteColorOptions, Theme, alpha, createTheme as createMuiTheme } from '@mui/material/styles';
import createPalette from '@mui/material/styles/createPalette';
import { listenIsMobile } from './models/Hooks';
import { getTenant, listenTenant } from './models/Tenant';
import { isMobile } from './utils';
import { ruRU as muiRU } from '@mui/material/locale';
import { ruRU as dpRU } from '@mui/x-date-pickers/locales';

var theme: Theme;

export const primary = '#0260F9';
export const info = '#D3E9FF';
export const getTheme = () => theme;

declare module "@mui/material/styles" {
    interface TypographyVariants {
        menu: React.CSSProperties;
        menuHover: React.CSSProperties;
        body3: React.CSSProperties;
        body4: React.CSSProperties;
        small: React.CSSProperties;
    }

    interface TypographyVariantsOptions {
        menu?: React.CSSProperties;
        menuHover: React.CSSProperties;
        body3: React.CSSProperties;
        body4: React.CSSProperties;
        small: React.CSSProperties;
    }

    interface PaletteOptions {
        button?: PaletteColorOptions;
    }
}

declare module '@mui/material' {
    interface ButtonPropsColorOverrides {
        button: true
    }
}

declare module '@mui/material/Typography' {
    interface TypographyPropsVariantOverrides {
        menu: true;
        menuHover: true;
        body3: true;
        body4: true;
        small: true;
    }
}

function createTheme() {
    const tenant = getTenant();
    const ism = isMobile();

    const palette = createPalette({
        background: {
            default: 'linear-gradient(212.78deg, #FCFDFF 21.68%, #FBFBFE 94.47%)'
        },
        primary: {
            main: tenant?.PrimaryColor || primary,
            info: tenant?.InfoColor || info
        },
        info: {
            contrastText: primary,
            main: '#D3E9FF',
            light: alpha('#D3E9FF', 0.5),
            dark: alpha('#D3E9FF', 0.9)
        },
        secondary: {
            main: '#1C252E',
            light: '#FBF8F3'
        },
        text: {
            primary: '#1E1E1E',
            disabled: '#7B818F'
        }
    });

    theme = createMuiTheme({
        cssVariables: true,
        palette,
        components: {
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        backgroundColor: '#FFF'
                    }
                }
            },
            MuiAlert: {
                styleOverrides: {
                    root: ism ? {
                        paddingTop: 0,
                        paddingBottom: 0
                    } : null,
                    message: {
                        fontSize: ism ? 10 : undefined,
                        fontWeight: ism ? 500 : undefined,
                        flex: 1,
                        textAlign: 'center',
                        alignSelf: 'center'
                    }
                }
            },
            MuiToolbar: {
                defaultProps: {
                    color: 'transparent'
                },
                styleOverrides: ism ? {
                    root: {
                        minHeight: 0
                    }
                } : undefined
            },
            MuiPaper: {
                styleOverrides: {
                    elevation1: {
                        boxShadow: 'rgba(145, 158, 171, 0.16) 0px 1px 2px 0px',
                        margin: 2
                    },
                    elevation2: {
                        boxShadow: '0px 3px 10px 0px #3A5E811A'
                    },
                    rounded: {
                        borderRadius: ism ? '.5rem' : '.75rem'
                    },
                    root: {
                        '& > .MuiFormControl-root:first-child': {
                            marginTop: '1rem'
                        }
                    }
                }
            },
            MuiTab: {
                styleOverrides: {
                    root: {
                        textTransform: 'none',
                        fontWeight: 500,
                        borderRadius: 0,
                        '&.Mui-selected': {
                            color: "secondary",
                            fontWeight: 600
                        }
                    }
                }
            },
            MuiCardHeader: {
                defaultProps: {
                    titleTypographyProps: {
                        variant: ism ? 'h2' : 'h1'
                    }
                }
            },
            MuiSwitch: {
                defaultProps: {
                    color: 'primary'
                }
            },
            MuiCheckbox: {
                defaultProps: {
                    color: 'primary'
                }
            },
            MuiButtonBase: {
                styleOverrides: {
                    root: {
                        textTransform: 'none'
                    }
                }
            },
            MuiToggleButtonGroup: {
                defaultProps: {
                    size: "small"
                }
            },
            MuiToggleButton: {
                defaultProps: {
                    size: "small"
                }
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        position: 'relative',
                        transform: 'none',
                        fontSize: ism ? '.714em' : '.857em'
                    }
                },
                defaultProps: {
                    translate: 'no',
                    disableAnimation: true
                }
            },
            MuiInputBase: {
                variants: [
                    {
                        props: p => p.disabled,
                        style: {
                            [`& > .${outlinedInputClasses.notchedOutline}`]: {
                                borderWidth: '0 0 1px',
                                borderRadius: 0
                            },
                            [`& > .${inputBaseClasses.input}.Mui-disabled`]: {
                                color: palette.text.primary,
                                '-webkit-text-fill-color': palette.text.primary,
                                paddingLeft: 0,
                                paddingRight: 0
                            },
                            [`& > .${selectClasses.iconOutlined}`]: {
                                display: 'none'
                            },
                            paddingLeft: '0!important',
                            paddingRight: '0!important'
                        }
                    }
                ],
                styleOverrides: ism ? {
                    root: {
                        fontSize: 14
                    }
                } : undefined
            },
            MuiOutlinedInput: {
                defaultProps: {
                    size: "small",
                    fullWidth: true,
                    minRows: 2,
                    maxRows: 6,
                    notched: false,

                }
            },
            MuiInputAdornment: {
                styleOverrides: {
                    root: {
                        marginLeft: 0
                    }
                }
            },
            MuiFormLabel: {/**
            styleOverrides: {
                root: {
                    marginTop: 8,
                    fontWeight: 500,
                    fontSize: '1.0745rem'
                }
            }/**/
            },
            MuiFormControlLabel: {
                styleOverrides: {
                    root: {
                        position: 'relative',
                        transform: 'none',
                        fontSize: ism ? '.714em' : '.857em'
                    }
                },
                defaultProps: {
                    translate: 'no',
                    disableAnimation: true
                }
            },
            MuiFormControl: {
                defaultProps: {
                    fullWidth: true
                }
            },
            MuiButton: {
                styleOverrides: {
                    root: {
                        textTransform: 'none',
                        boxShadow: 'none'
                    },
                    outlined: {
                        backgroundColor: '#fff'
                    }
                },
                variants: ism ? [
                    {
                        props: p => p.size === 'small',
                        style: {
                            fontSize: 10
                        }
                    },
                    {
                        props: p => p.size === 'medium',
                        style: {
                            fontSize: 14,
                            lineHeight: 1.4,
                            padding: 10
                        }
                    },
                    {
                        props: p => p.size === 'large',
                        style: {
                            padding: 12
                        }
                    }
                ] : undefined
            },
            MuiLink: {
                styleOverrides: {
                    root: {
                        textDecoration: 'none'
                    }
                }
            },
            MuiMenu: {
                defaultProps: {
                    MenuListProps: {
                        disablePadding: true
                    }
                }
            },
            MuiMenuItem: {
                styleOverrides: {
                    root: {
                        justifyContent: 'space-between',
                        gap: 8,
                        fontSize: 12,
                        minHeight: 44,
                        "& .MuiSvgIcon-root": {
                            fontSize: '1.66em'
                        }
                    }
                }
            },
            MuiTextField: {
                defaultProps: {
                    size: 'small'
                }
            },
            MuiPopover: {
                styleOverrides: {
                    paper: {
                        [`& .${menuItemClasses.root}`]: {
                            fontSize: ism ? 14 : 18,
                            [`&>.${checkboxClasses.root}`]: {
                                fontSize: '.75em',
                                padding: 4
                            },
                            [`&+.${dividerClasses.root}`]: ism ? {
                                margin: 0
                            } : undefined
                        }
                    }
                }
            }
        },
        typography: {
            ...(ism ? {
                h1: { fontSize: 20, lineHeight: 1.3, fontWeight: 700 },
                h2: { fontSize: 16, lineHeight: 1, fontWeight: 700 },
                h3: { fontSize: 14, lineHeight: 1, fontWeight: 600 },
                h4: { fontSize: 12, lineHeight: 1, fontWeight: 600 },
                h5: { fontSize: 10, lineHeight: 1.2, fontWeight: 600 },
                body1: { fontSize: 16, lineHeight: 1.4, fontWeight: 400 },
                body2: { fontSize: 12, lineHeight: 1, fontWeight: 400 },
                body3: { fontSize: 10, lineHeight: 1, fontWeight: 500 },
                body4: { fontSize: 10, lineHeight: 1, fontWeight: 400 },
                caption: { fontSize: 10, lineHeight: 1, fontWeight: 400 }, // body 4
                subtitle2: { fontSize: 10, lineHeight: 1, fontWeight: 400 },
                menu: { fontSize: 16, lineHeight: 1.36625, fontWeight: 400 },
                overline: { fontSize: 8, lineHeight: 1, fontWeight: 400 },
            } : {
                h1: { fontSize: 24, lineHeight: 1.2, fontWeight: 700 },
                h2: { fontSize: 16, lineHeight: 1, fontWeight: 700 },
                h3: { fontSize: 14, lineHeight: 1, fontWeight: 600 },
                h4: { fontSize: 12, lineHeight: 1, fontWeight: 600 },
                h5: { fontSize: 10, lineHeight: 1.2, fontWeight: 600 },
                menu: { fontSize: 16, lineHeight: 1.36625, fontWeight: 400 },
                body1: { fontSize: 18, lineHeight: 1.4, fontWeight: 400 },
                body2: { fontSize: 14, lineHeight: 1.2, fontWeight: 500 },
                body3: { fontSize: 10, lineHeight: 1, fontWeight: 500 },
                body4: { fontSize: 10, lineHeight: 1, fontWeight: 400 },
                caption: { fontSize: 12, lineHeight: 1.2, fontWeight: 400 },
                subtitle2: { fontSize: 10, lineHeight: 1, fontWeight: 400 },
                overline: { fontSize: 8, lineHeight: 1, fontWeight: 400 },
            }),
            fontFamily: [
                'Manrope',
                '-apple-system',
                'BlinkMacSystemFont',
                '"Segoe UI"',
                'Roboto',
                '"Helvetica Neue"',
                'Arial',
                'sans-serif',
                '"Apple Color Emoji"',
                '"Segoe UI Emoji"',
                '"Segoe UI Symbol"',
            ].join(',')
        }
    }, muiRU, dpRU);
}

createTheme();

listenTenant(createTheme);
listenIsMobile(createTheme);